import { FC, useEffect, useMemo, useState } from 'react';
import { Conditional } from '../../Common/Conditional';
import { InputMode, UserInput } from '../../Common/UserInput';
import { RadioDialog } from './RadioDialog';
import { Period } from './Invoice';
import { ResourceText, ShopContent } from '../../../types/cms_shared_types';
import { UserInput as UserInputValues } from '../../../types/cms_shared_types';
import { findUserInputResourceByKey } from '../../../helper/cms';
import { UserSelectInput } from '../../Common/UserSelectInput';
import { EdiFormat, InvoiceFormInputs, InvoiceMethod } from '../../../types/api_shared_types';

interface InvoiceMethodsProps {
  cmsShopContent: ShopContent;
  values: InvoiceFormInputs;
  setInvoiceMethod: (value: string) => void;
}

export const InvoiceMethods: FC<InvoiceMethodsProps> = ({ cmsShopContent, values, setInvoiceMethod }) => {
  const [emailInput, setEmailInput] = useState<UserInputValues>();

  const availableMethods = useMemo<ResourceText[]>(() => {
    const emailMethod: ResourceText = {
      key: cmsShopContent.invoiceStep.emailInvoice.key,
      value: cmsShopContent.invoiceStep.emailInvoice.title,
    };

    const ediMethod: ResourceText = {
      key: cmsShopContent.invoiceStep.ediInvoice.key,
      value: cmsShopContent.invoiceStep.ediInvoice.title,
    };

    const paperMethod: ResourceText = {
      key: cmsShopContent.invoiceStep.paperInvoice.key,
      value: cmsShopContent.invoiceStep.paperInvoice.title,
    };

    const allMethods = [emailMethod, ediMethod, paperMethod];

    if (values.invoicePeriodInput === Period.MONTHLY) {
      return allMethods.filter((method) => method.key !== InvoiceMethod.PAPER);
    }

    return allMethods;
  }, [cmsShopContent.invoiceStep, values.invoicePeriodInput]);

  useEffect(() => {
    setEmailInput(findUserInputResourceByKey('email_address_pdf_input', cmsShopContent.invoiceStep.userInputs));
  }, [cmsShopContent.invoiceStep.userInputs, setEmailInput]);

  return (
    <div className="flex flex-col w-full gap-y-4 sm:gap-y-0">
      <Conditional condition={availableMethods.length > 0}>
        <div className=" sm:h-[78px] sm:mb-8">
          <RadioDialog
            prompt={cmsShopContent.invoiceStep.invoiceMethodTitle}
            options={availableMethods}
            selectedKey={values.selectedInvoiceMethod}
            setSelectedKey={(value: string): void => setInvoiceMethod(value)}
            isSelected={(selectedKey: string, option: ResourceText): boolean => option.key === selectedKey}
            renderTextInsteadOfRadioItems={false}
          />
        </div>
      </Conditional>
      <Conditional condition={values.selectedInvoiceMethod === InvoiceMethod.PAPER}>
        <div className="text-sm font-medium text-text-dark">
          {cmsShopContent.invoiceStep.paperInvoiceExtraDescription}
        </div>
      </Conditional>

      <Conditional condition={values.selectedInvoiceMethod === InvoiceMethod.EMAIL}>
        <UserInput
          name="emailAddressPdfInput"
          prompt={emailInput?.prompt}
          placeholder={emailInput?.placeholder}
          inputMode={InputMode.Email}
        />
      </Conditional>

      <Conditional condition={values.selectedInvoiceMethod === InvoiceMethod.EDI}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          <UserSelectInput
            name="ediFormat"
            prompt={cmsShopContent.invoiceStep.ediInvoice.formatTitle}
            placeholder={cmsShopContent.invoiceStep.ediInvoice.chooseFormat}
            options={cmsShopContent.invoiceStep.ediInvoice.formats.map((ediFormat) => ({
              displayValue: ediFormat.value,
              key: ediFormat.key,
            }))}
          />
          <Conditional condition={!!values.ediFormat}>
            <UserInput
              name="ediReference"
              prompt={cmsShopContent.invoiceStep.ediInvoice.reference}
              inputMode={InputMode.Text}
              maxLength={60}
            />
          </Conditional>
          <Conditional condition={values.ediFormat === EdiFormat.PEPPOL}>
            <UserInput
              name="peppolId"
              prompt={cmsShopContent.invoiceStep.ediInvoice.peppolIdTitle}
              placeholder={cmsShopContent.invoiceStep.ediInvoice.peppolIdPlaceholder}
              inputMode={InputMode.Text}
            />
          </Conditional>
          <Conditional condition={values.ediFormat === EdiFormat.SVEFAKTURA}>
            <UserSelectInput
              name="ediOperator"
              prompt={cmsShopContent.invoiceStep.ediInvoice.operatorTitle}
              placeholder={cmsShopContent.invoiceStep.ediInvoice.chooseOperator}
              options={cmsShopContent.invoiceStep.ediInvoice.ediOperators.map((operator) => ({
                displayValue: operator.name,
                key: operator.key,
              }))}
            />
            <UserInput
              name="gln"
              prompt={cmsShopContent.invoiceStep.ediInvoice.gln}
              placeholder={cmsShopContent.invoiceStep.ediInvoice.glnPlaceholder}
              inputMode={InputMode.Number}
            />
          </Conditional>
        </div>
      </Conditional>
    </div>
  );
};
